import { haversineMeters } from './driver-attendance';

/**
 * Trạng thái xe theo GPS Bình Minh (anh Tiến 20–21/10/2026: "ghi trạng thái GPS và tính lại, chứ không theo định vị").
 * Lấy thẳng ô GPS gửi về, không suy từ tọa độ:
 *  - Key = khóa điện (true = nổ máy), IsStop = xe đứng yên, BeginStop = giờ bắt đầu đứng (giờ VN),
 *    TimeUpdate = giờ thiết bị gửi gần nhất (giờ VN). StopTime = giờ nhà cung cấp − BeginStop nên không cần lưu.
 *  - Mẫu thật 21/10/2026 09:03: xem /mnt/project-files/doi-xe/gps-binhminh-truong-du-lieu.md.
 * Trạng thái: Đang chạy / Dừng nổ máy / Đỗ tắt máy / Cẩu hàng (tài xế đang bấm cẩu, xe đứng, máy nổ) / Mất tín hiệu.
 */
export type GpsVehicleStatus = 'running' | 'idle' | 'parked' | 'crane' | 'offline';
export const GPS_VEHICLE_STATUSES: GpsVehicleStatus[] = ['running', 'idle', 'parked', 'crane', 'offline'];
export const GPS_VEHICLE_STATUS_LABELS: Record<GpsVehicleStatus, string> = {
  running: 'Đang chạy',
  idle: 'Dừng nổ máy',
  parked: 'Đỗ tắt máy',
  crane: 'Cẩu hàng',
  offline: 'Mất tín hiệu',
};

/** Thiết bị không gửi quá số phút này → Mất tín hiệu (mẫu thật: xe tắt máy vẫn gửi ~7 phút/lần) */
export const GPS_OFFLINE_MINUTES = 30;
/** Đứng (dừng hoặc đỗ) lâu hơn số phút này ngoài bãi / kho / chỗ giao → báo quản lý xe + GĐ */
export const GPS_LONG_STOP_MINUTES = 10;
/** Bán kính coi là "ở" bãi, kho, trạm dầu, điểm giao */
export const GPS_KNOWN_PLACE_RADIUS_M = 300;
/** Hai mẫu cách nhau quá số phút này thì khoảng giữa là "không có số" (server dừng, mất kết nối nhà cung cấp) */
export const GPS_SAMPLE_GAP_MINUTES = 10;

const VN_MS = 7 * 3_600_000;

/** Giờ VN không có múi giờ của GPS ("2026/10/21 08:59:31", "2026-10-21T08:59:34") → Date; sai định dạng → null. */
export function parseGpsVnTime(s: unknown): Date | null {
  if (typeof s !== 'string') return null;
  const m = /^(\d{4})[-/](\d{2})[-/](\d{2})[T ](\d{2}):(\d{2})(?::(\d{2}))?/.exec(s.trim());
  if (!m) return null;
  const t = Date.UTC(+m[1]!, +m[2]! - 1, +m[3]!, +m[4]!, +m[5]!, +(m[6] ?? 0)) - VN_MS;
  return Number.isFinite(t) ? new Date(t) : null;
}

export interface GpsStatusInput {
  sampledAt: Date;
  gpsTime: Date | null;
  speedKmh: number | null;
  keyOn: boolean | null;
  isStop: boolean | null;
  gpsOk: boolean | null;
  /** Điện áp nguồn GPS (ô InputPower, V). Không có thì chỉ dựa vào khóa điện. */
  voltage?: number | null;
  /** Tài xế đang bấm cẩu (lượt cẩu chưa Xong) */
  craneOpen: boolean;
}

/**
 * Ngưỡng điện áp coi là máy đang nổ (anh Tiến 22/10/2026 "xe tắt máy nhưng GPS vẫn bật"):
 * ô Key lấy từ dây ACC, có xe đấu ACC vào nguồn thường trực / để chìa nấc ON nên Key bật mà máy tắt.
 * Máy nổ thì máy phát sạc: hệ 24V lên ~27–28V, hệ 12V lên ~13,8–14,4V; tắt máy chỉ còn điện ắc quy (24–25,5V / 12,4–12,8V).
 * Điện áp ≥ GPS_24V_SYSTEM_MIN coi là hệ 24V. Ngưỡng tạm theo thông số ắc quy, chỉnh lại khi đối chiếu số gốc (#419).
 */
export const GPS_ENGINE_ON_VOLT_24 = 26;
export const GPS_ENGINE_ON_VOLT_12 = 13.3;
export const GPS_24V_SYSTEM_MIN = 18;
/** Dưới mức này coi như GPS không đo được điện áp (0 / mất nguồn) → bỏ qua, dựa vào khóa điện */
export const GPS_VOLT_MIN_VALID = 8;

/** Máy có đang nổ: khóa điện bật và (nếu GPS gửi điện áp) điện áp đủ mức máy phát đang sạc */
export function gpsEngineOn(keyOn: boolean | null, voltage?: number | null): boolean {
  if (!keyOn) return false;
  if (voltage == null || !Number.isFinite(voltage) || voltage < GPS_VOLT_MIN_VALID) return true;
  return voltage >= (voltage >= GPS_24V_SYSTEM_MIN ? GPS_ENGINE_ON_VOLT_24 : GPS_ENGINE_ON_VOLT_12);
}

export function gpsVehicleStatus(i: GpsStatusInput): GpsVehicleStatus {
  if (i.gpsOk === false || !i.gpsTime || i.sampledAt.getTime() - i.gpsTime.getTime() > GPS_OFFLINE_MINUTES * 60_000) return 'offline';
  // Anh Tiến 23/10/2026 "kiểm soát trạng thái cẩu": cẩu chỉ tính khi tài xế đang bấm cẩu VÀ máy nổ (bơm cẩu chạy bằng máy xe).
  // Bấm cẩu mà máy tắt → Đỗ tắt máy (job tự chốt Xong cẩu sau CRANE_ENGINE_OFF_MINUTES); xe chạy → Đang chạy.
  if (i.isStop === false || (i.speedKmh ?? 0) > 0) return 'running';
  const engine = gpsEngineOn(i.keyOn, i.voltage);
  if (i.craneOpen && engine) return 'crane';
  return engine ? 'idle' : 'parked';
}

// ---------------------------------------------------------------------------
// Dòng thời gian trong ngày
// ---------------------------------------------------------------------------
export interface GpsStatusSample {
  at: Date;
  status: GpsVehicleStatus;
  /** BeginStop: giờ bắt đầu đứng (khi đang dừng / đỗ / cẩu) */
  stopSince: Date | null;
  lat: number | null;
  lng: number | null;
  /** Km trong ngày (GPS về 0 lúc nửa đêm) */
  dayKm: number | null;
}
export interface GpsSegment {
  status: GpsVehicleStatus | 'nodata';
  from: Date;
  to: Date;
  minutes: number;
  lat: number | null;
  lng: number | null;
}

const minutesOf = (a: Date, b: Date) => Math.max(0, Math.round((b.getTime() - a.getTime()) / 60_000));

/**
 * Ghép mẫu thành đoạn trạng thái trong [dayStart, dayEnd) (dayEnd = bây giờ nếu là hôm nay).
 * Mỗi mẫu kéo tới mẫu sau (tối đa GPS_SAMPLE_GAP_MINUTES, quá thì "không có số"). Đoạn đứng bắt đầu từ BeginStop
 * nếu BeginStop sớm hơn mẫu đầu đoạn (lùi phần cuối đoạn trước), để giờ đứng đúng theo GPS chứ không theo giờ lấy mẫu.
 */
export function gpsTimeline(samples: GpsStatusSample[], dayStart: Date, dayEnd: Date): GpsSegment[] {
  const gap = GPS_SAMPLE_GAP_MINUTES * 60_000;
  const s = samples.filter((x) => x.at < dayEnd).sort((a, b) => a.at.getTime() - b.at.getTime());
  const raw: { status: GpsSegment['status']; from: number; to: number; lat: number | null; lng: number | null }[] = [];
  const push = (status: GpsSegment['status'], from: number, to: number, lat: number | null = null, lng: number | null = null) => {
    from = Math.max(from, dayStart.getTime());
    to = Math.min(to, dayEnd.getTime());
    if (to <= from) return;
    const last = raw[raw.length - 1];
    if (last && last.status === status && last.to >= from) {
      last.to = Math.max(last.to, to);
      if (last.lat === null) Object.assign(last, { lat, lng });
      return;
    }
    raw.push({ status, from, to, lat, lng });
  };
  let cursor = dayStart.getTime();
  for (let k = 0; k < s.length; k++) {
    const x = s[k]!;
    const at = x.at.getTime();
    const next = s[k + 1]?.at.getTime() ?? dayEnd.getTime();
    let from = at;
    if ((x.status === 'idle' || x.status === 'parked') && x.stopSince && x.stopSince.getTime() < at) {
      // Lùi đầu đoạn về BeginStop, chỉ cắt đoạn chạy / không có số trước đó (không đè đoạn cẩu, dừng, đỗ đã có)
      from = Math.max(x.stopSince.getTime(), dayStart.getTime());
      const soft = (st: GpsSegment['status']) => st === 'running' || st === 'nodata';
      while (raw.length && raw[raw.length - 1]!.from >= from && soft(raw[raw.length - 1]!.status)) raw.pop();
      const last = raw[raw.length - 1];
      if (last && last.to > from) {
        if (soft(last.status)) last.to = from;
        else if (last.status !== x.status) from = last.to;
      }
      if (from > at) from = at;
      cursor = Math.min(cursor, from);
    }
    if (from > cursor) push('nodata', cursor, from);
    const end = Math.min(next, at + gap);
    push(x.status, from, end, x.lat, x.lng);
    cursor = end;
  }
  if (cursor < dayEnd.getTime()) push('nodata', cursor, dayEnd.getTime());
  return raw.map((r) => ({ status: r.status, from: new Date(r.from), to: new Date(r.to), minutes: minutesOf(new Date(r.from), new Date(r.to)), lat: r.lat, lng: r.lng }));
}

// ---------------------------------------------------------------------------
// Nơi quen (bãi, kho, trạm dầu, điểm giao trong ngày) và đứng lâu
// ---------------------------------------------------------------------------
export interface KnownPlace {
  kind: 'depot' | 'warehouse' | 'station' | 'stop';
  name: string;
  /** Điểm giao hàng cho khách (điểm dừng giao của chuyến, phiếu chọn xe thẳng); false/không có = lấy hàng, bãi, kho, trạm dầu */
  drop?: boolean;
  lat: number;
  lng: number;
  radiusM?: number;
}
export function knownPlaceAt(lat: number | null, lng: number | null, places: KnownPlace[]): KnownPlace | null {
  if (lat === null || lng === null) return null;
  for (const p of places) if (haversineMeters(lat, lng, p.lat, p.lng) <= (p.radiusM ?? GPS_KNOWN_PLACE_RADIUS_M)) return p;
  return null;
}

/** Đoạn đứng (dừng / đỗ) ≥ GPS_LONG_STOP_MINUTES ở ngoài nơi quen. */
export function longStopsOutside(segments: GpsSegment[], places: KnownPlace[]): GpsSegment[] {
  return segments.filter((g) => (g.status === 'idle' || g.status === 'parked') && g.minutes >= GPS_LONG_STOP_MINUTES && !knownPlaceAt(g.lat, g.lng, places));
}

/**
 * Nghi cẩu không bấm: xe cẩu dừng nổ máy ≥ GPS_LONG_STOP_MINUTES ở điểm giao trong ngày (không phải bãi / kho / trạm dầu)
 * mà tài xế không bấm cẩu.
 */
export function craneSuspects(segments: GpsSegment[], places: KnownPlace[], isCrane: boolean): GpsSegment[] {
  if (!isCrane) return [];
  return segments.filter((g) => g.status === 'idle' && g.minutes >= GPS_LONG_STOP_MINUTES && knownPlaceAt(g.lat, g.lng, places)?.kind === 'stop');
}

// ---------------------------------------------------------------------------
// Trạng thái trên bản đồ xe realtime (anh Tiến 22/10/2026: "Để biết xe ở đâu. Đang: chạy - dừng - dỡ - cẩu hàng")
// ---------------------------------------------------------------------------
export type GpsLiveStatus = GpsVehicleStatus | 'unloading';
export const GPS_LIVE_STATUSES: GpsLiveStatus[] = ['running', 'idle', 'parked', 'unloading', 'crane', 'offline'];
export const GPS_LIVE_STATUS_LABELS: Record<GpsLiveStatus, string> = { ...GPS_VEHICLE_STATUS_LABELS, unloading: 'Dỡ hàng' };
/** Đứng ở điểm giao ít nhất số phút này mới tính "Dỡ hàng" (dừng đèn đỏ / chạy chậm gần khách không tính) */
export const GPS_UNLOAD_MIN_MINUTES = 3;

/**
 * Dừng nổ máy / đỗ tắt máy trong bán kính điểm giao của xe (chuyến hoặc phiếu chọn xe thẳng) từ GPS_UNLOAD_MIN_MINUTES phút
 * → "Dỡ hàng" (GPS không gửi BeginStop thì tính luôn). `place` = điểm giao gần xe (chỉ xét nơi có drop). Cẩu hàng (tài xế bấm) giữ nguyên; các trạng thái khác giữ nguyên.
 */
export function gpsLiveStatus(i: { status: GpsVehicleStatus; stopSince: Date | null; at: Date; place: KnownPlace | null }): GpsLiveStatus {
  if ((i.status !== 'idle' && i.status !== 'parked') || !i.place?.drop) return i.status;
  if (!i.stopSince) return 'unloading';
  return i.at.getTime() - i.stopSince.getTime() >= GPS_UNLOAD_MIN_MINUTES * 60_000 ? 'unloading' : i.status;
}

/** Xuất bãi / về bãi trong ngày theo điểm về của xe (anh Tiến 21/10/2026: "gán địa điểm cho xe, để xác định khi nào tài về"). */
export interface GpsHomeTrip {
  /** Lần đầu rời điểm về trong ngày (mẫu cuối còn ở điểm về trước khi ra ngoài); null = không rời, hoặc đầu ngày đã ở ngoài */
  leftAt: Date | null;
  /** Giờ về lần cuối: xe vào điểm về và đứng (BeginStop nếu có); null = chưa về hoặc không rời bãi */
  returnedAt: Date | null;
  /** Mẫu có tọa độ mới nhất ở điểm về; null = không có mẫu nào có tọa độ */
  atHome: boolean | null;
}

/**
 * Duyệt mẫu theo giờ: ra khỏi điểm về → đi; vào lại điểm về mà đứng (dừng nổ máy / đỗ / cẩu) → về, giờ về = lúc bắt đầu đứng.
 * Chạy ngang qua điểm về không tính là về. Mẫu mất tín hiệu hoặc không tọa độ bỏ qua.
 */
export function gpsHomeTrip(samples: GpsStatusSample[], homes: KnownPlace[]): GpsHomeTrip {
  const sorted = [...samples].sort((a, b) => a.at.getTime() - b.at.getTime());
  let leftAt: Date | null = null;
  let returnedAt: Date | null = null;
  let atHome: boolean | null = null;
  let away = false;
  let lastHomeAt: Date | null = null;
  let lastOutAt: Date | null = null;
  for (const s of sorted) {
    if (s.status === 'offline' || s.lat === null || s.lng === null) continue;
    if (!knownPlaceAt(s.lat, s.lng, homes)) {
      if (!away && lastHomeAt) leftAt ??= lastHomeAt;
      away = true;
      returnedAt = null;
      atHome = false;
      lastOutAt = s.at;
      continue;
    }
    atHome = true;
    lastHomeAt = s.at;
    if (away && s.status !== 'running') {
      returnedAt = s.stopSince && lastOutAt && s.stopSince > lastOutAt ? s.stopSince : s.at;
      away = false;
    }
  }
  return { leftAt, returnedAt, atHome };
}

// ---------------------------------------------------------------------------
// Báo cáo theo khung giờ cho điều phối (anh Tiến 20/10/2026)
// ---------------------------------------------------------------------------
export const GPS_REPORT_SLOTS: { from: string; to: string }[] = [
  { from: '07:00', to: '08:30' },
  { from: '08:30', to: '10:30' },
  { from: '10:30', to: '11:30' },
  { from: '11:30', to: '13:30' },
  { from: '13:30', to: '14:30' },
  { from: '14:30', to: '15:30' },
  { from: '15:30', to: '16:30' },
  { from: '16:30', to: '17:30' },
  { from: '17:30', to: '20:30' },
  { from: '20:30', to: '22:00' },
  { from: '22:00', to: '24:00' },
];
/** Giờ gửi báo cáo (giờ VN): 17:30 cho các khung tới 17:30 hôm nay, 00:30 cho cả ngày hôm qua */
export const GPS_REPORT_SEND_TIMES = ['17:30', '00:30'] as const;

/** Mốc giờ VN của ngày `day` (YYYY-MM-DD), "24:00" = 00:00 hôm sau */
export function vnTimeOn(day: string, hhmm: string): Date {
  const [h, m] = hhmm.split(':').map(Number) as [number, number];
  return new Date(Date.parse(`${day}T00:00:00Z`) + (h * 60 + m) * 60_000 - VN_MS);
}

export type GpsMinutes = Record<GpsVehicleStatus | 'nodata', number>;
export interface GpsSlotSummary {
  from: string;
  to: string;
  minutes: GpsMinutes;
  /** Km chạy trong khung (theo km trong ngày của GPS); null = không có số */
  km: number | null;
  /** Số lần đứng lâu ngoài nơi quen bắt đầu trong khung */
  longStops: number;
}
const zero = (): GpsMinutes => ({ running: 0, idle: 0, parked: 0, crane: 0, offline: 0, nodata: 0 });

/** Km trong ngày tại thời điểm t: mẫu gần nhất trước t (chưa có mẫu = 0, đầu ngày); không mẫu nào trong ngày → null. */
function dayKmAt(samples: GpsStatusSample[], t: number): number | null {
  const withKm = samples.filter((s) => s.dayKm !== null);
  if (!withKm.length) return null;
  let best: GpsStatusSample | null = null;
  for (const s of withKm) if (s.at.getTime() <= t && (!best || s.at > best.at)) best = s;
  return best ? best.dayKm : 0;
}

export function gpsSlotReport(day: string, samples: GpsStatusSample[], segments: GpsSegment[], longStops: GpsSegment[], until: Date | null = null): GpsSlotSummary[] {
  const out: GpsSlotSummary[] = [];
  for (const sl of GPS_REPORT_SLOTS) {
    const a = vnTimeOn(day, sl.from).getTime();
    const b = Math.min(vnTimeOn(day, sl.to).getTime(), until?.getTime() ?? Infinity);
    if (b <= a) break;
    const minutes = zero();
    for (const g of segments) {
      const lo = Math.max(a, g.from.getTime());
      const hi = Math.min(b, g.to.getTime());
      if (hi > lo) minutes[g.status] += Math.round((hi - lo) / 60_000);
    }
    const k1 = dayKmAt(samples, a);
    const k2 = dayKmAt(samples, b);
    out.push({
      from: sl.from,
      to: sl.to,
      minutes,
      km: k1 === null || k2 === null ? null : Math.round(Math.max(0, k2 - k1) * 10) / 10,
      longStops: longStops.filter((g) => g.from.getTime() >= a && g.from.getTime() < b).length,
    });
  }
  return out;
}

/** "1 giờ 05" / "35 phút" */
export function fmtMinutesVN(m: number): string {
  if (m < 60) return `${m} phút`;
  const h = Math.floor(m / 60);
  const r = m % 60;
  return r ? `${h} giờ ${String(r).padStart(2, '0')}` : `${h} giờ`;
}
